import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Settings, Database, Trash, Info } from 'lucide-react';

// Variable Configuration Table Component
const VariableConfigurationTable = ({ 
  systemVariables = [],
  dynamicVariables = [], 
  onUpdateVariableConfig,
  variableConfigs = {}, // Store variable configurations
  // Base-level variable configuration (url, header)
  variableConfiguration = { url: '', header: '' },
  onBaseConfigChange = () => {}
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUrlInfoOpen, setIsUrlInfoOpen] = useState(false);
  // Combine system and dynamic variables
  const allVariables = [
    ...systemVariables.map(v => ({ ...v, type: 'system' })),
    ...dynamicVariables.map(v => ({ ...v, type: 'dynamic' }))
  ];

  const handleRequiredChange = (variableName, isRequired) => {
    if (onUpdateVariableConfig) {
      onUpdateVariableConfig(variableName, 'required', isRequired);
    }
  };

  const handleDefaultValueChange = (variableName, defaultValue) => {
    if (onUpdateVariableConfig) {
      onUpdateVariableConfig(variableName, 'defaultValue', defaultValue);
    }
  };

  const handleDeleteVariable = (variableName) => {
    if (onUpdateVariableConfig) {
      onUpdateVariableConfig(variableName, 'delete', true);
    }
  };

  const getVariableConfig = (variableName) => {
    return variableConfigs[variableName] || { required: false, defaultValue: '' };
  };

  return (
    <div className="border-t pt-6">
      <div 
        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
            <Database className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">Variable Configuration</h3>
            <p className="text-sm text-gray-600">Configure default values and requirements for variables</p>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {allVariables.length} variables available
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Base-level URL and Header configuration */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">External URL (Optional)</label>
                <button
                  type="button"
                  onClick={() => setIsUrlInfoOpen(prev => !prev)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="External URL info"
                  title="Click for details"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
              {isUrlInfoOpen && (
                <div className="absolute right-0 z-10 mt-1 w-80 bg-white border border-gray-200 shadow-lg rounded-md p-3 text-xs text-gray-700">
                  <div className="font-medium text-gray-900 mb-1">Request payload</div>
                  <div className="space-y-1">
                    <p>We include <code>mobile_number</code> and <code>agent_id</code> when calling your URL.</p>
                    <p><span className="font-medium">GET</span>: sent as query params <code>?mobile_number=...&amp;agent_id=...</code></p>
                    <p><span className="font-medium">POST</span>: JSON body <code>{'{ "mobile_number": "...", "agent_id": "..." }'}</code></p>
                  </div>
                </div>
              )}
              <input
                type="text"
                value={variableConfiguration?.url || ''}
                onChange={(e) => onBaseConfigChange('url', String(e.target.value || '').trim())}
                maxLength={1000}
                placeholder="https://example.com/endpoint"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">HTTP Method</label>
              <ModernTimeDropdown ariaLabel="HTTP Method" value={variableConfiguration?.method || 'GET'} onChange={(e) => onBaseConfigChange('method', e.target.value)} options={['GET', 'POST']} width="w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Request Headers (semicolon-separated key:value; key2:value2)</label>
              <input
                type="text"
                value={variableConfiguration?.header || ''}
                onChange={(e) => {
                  const raw = String(e.target.value || '');
                  const parts = raw
                    .split(';')
                    .map(s => s.trim())
                    .filter(Boolean)
                    .map(p => p.replace(/\s*:\s*/g, ':'));
                  const normalized = parts.join(';');
                  onBaseConfigChange('header', normalized);
                }}
                maxLength={1000}
                placeholder="Authorization:Bearer abc; X-Trace-Id:123"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 italic ml-4 mb-4">* If external URL doesn't provide expected output, default values will be used.</p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variable Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Required
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Default Value
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variable Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allVariables.map((variable, index) => {
                  const config = getVariableConfig(variable.name);
                  return (
                    <tr 
                      key={`${variable.name}-${index}`}
                      className={`hover:bg-gray-50 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                      }`}
                    >
                      {/* Variable Name */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <code className="text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                            {`{{${variable.name}}}`}
                          </code>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          variable.type === 'system' 
                            ? 'bg-gray-50 text-gray-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {variable.type}
                        </span>
                      </td>

                      {/* Required Checkbox */}
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={variable.type === 'system' ? true : config.required}
                            onChange={variable.type === 'system' ? undefined : (e) => handleRequiredChange(variable.name, e.target.checked)}
                            disabled={variable.type === 'system'}
                            className={`h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded ${
                              variable.type === 'system' 
                                ? 'cursor-not-allowed opacity-60' 
                                : 'cursor-pointer'
                            }`}
                          />
                        </div>
                      </td>

                      {/* Default Value Input */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {variable.type === 'system' ? (
                          <div className="flex items-center text-xs text-gray-500 italic">
                            System managed
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={config.defaultValue}
                            onChange={(e) => handleDefaultValueChange(variable.name, e.target.value)}
                            placeholder="Enter default value"
                            className="w-full max-w-xs px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        )}
                      </td>

                      {/* Description */}
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-900 max-w-xs">
                          {variable.description}
                        </div>
                      </td>
                      {/* Variable Type */}
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        {variable.variable_type}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        {variable.type === 'dynamic'  ? <button className="text-sm text-gray-500" onClick={() => handleDeleteVariable(variable.name)}>
                          <Trash className="w-4 h-4" />
                        </button> : (
                            <div className="text-xs text-gray-500">N/A</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {allVariables.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">No Variables Available</p>
              <p className="text-sm">Add custom variables using the variable insertion tool to see them here</p>
            </div>
          )}

          {/* Summary Footer */}
          {allVariables.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex justify-between items-center text-sm text-gray-600">
                <div className="flex gap-6">
                  <span>
                    Total: <span className="font-medium text-gray-900">{allVariables.length}</span>
                  </span>
                  <span>
                    System: <span className="font-medium text-blue-800">{systemVariables.length}</span>
                  </span>
                  <span>
                    Custom: <span className="font-medium text-purple-800">{dynamicVariables.length}</span>
                  </span>
                </div>
                <div>
                  Required: <span className="font-medium text-gray-900">
                    {Object.values(variableConfigs).filter(config => config.required).length}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VariableConfigurationTable;